import { useEffect, useState, useCallback } from "react";
import parse from "html-react-parser";
import NumberFormat from "react-number-format";
import { Container, ContainerView } from "../../components/Container";
import ArrowRightIcon2 from "../../components/Icons/ArrowRightIcon2";
import { useApp } from "../../contexts";
import StakeForOptionIcon from "./components/StakeForOptionIcon";
import StakeForVotingIcon from "./components/StakeForVotingIcon";
import {
  decimalSeparatorByCurrency,
  thousandSeparatorByCurrency,
} from "../../utils/Currency";
import WarningIcon from "./components/WarningIcon";
import { useNavigate } from "react-router-dom";
import ModalConnectWallet from "../stake-for-voting/components/ModalConnectWallet";
import { getUserStakeInfo } from "../../services/app";
import { convertHexBalanceToDecimal } from "../../utils/hepler";
import { formatNumberShort } from "../../utils/helper";

import { PolygonIcon } from "../../components/Icons/PolygonIcon";
import { EtherIcon } from "../../components/Icons/EtherIcon";
import { useWindowSize } from "../../hook/useWindowSize";

export default function StakePage() {
  const {
    web3Provider,
    masterContract,
    T,
    getCurrency,
    userWeb3Addresses,
    addressConnected,
  } = useApp();
  const navigate = useNavigate();
  const size = useWindowSize();
  const isMobile = size.width < 1024;

  const [stakeForOptionData, setStakeForOptionData] = useState({
    stakedBalance: 0,
    availableBalance: 0,
  });
  const [isLoadingStakeForOption, setIsLoadingStakeForOption] = useState(false);

  const [stakeForVotingData, setStakeForVotingData] = useState({
    stakedBalance: 0,
    totalNfts: 0,
  });
  const [isLoadingStakeForVoting, setIsLoadingStakeForVoting] = useState(false);

  const [isShowModalConnectWallet, setIsShowModalConnectWallet] =
    useState(false);

  // Fetch stake for option data
  const getStakeForOptionData = useCallback(async () => {
    try {
      setIsLoadingStakeForOption(true);
      const res = await getUserStakeInfo();
      if (res?.data) {
        setStakeForOptionData(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingStakeForOption(false);
    }
  }, []);
  useEffect(() => {
    getStakeForOptionData();
  }, [getStakeForOptionData]);

  // Fetch stake for voting data
  const getStakeForVotingData = useCallback(async () => {
    if (
      !masterContract ||
      !web3Provider ||
      !userWeb3Addresses ||
      !masterContract.sgtStakingContract
    ) {
      return;
    }
    try {
      setIsLoadingStakeForVoting(true);
      const promises = userWeb3Addresses.map((address) =>
        masterContract.sgtStakingContract.getStakesFromAddress(address)
      );
      const results = await Promise.all(promises);
      const stakes =
        results.flatMap(
          (res) =>
            res?.map((item) => ({
              amount: convertHexBalanceToDecimal(item[1].toString(), 8),
            })) ?? []
        ) ?? [];
      setStakeForVotingData({
        stakedBalance: stakes.reduce(
          (total, stake) => total + Number(stake.amount),
          0
        ),
        totalNfts: stakes?.length ?? 0,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingStakeForVoting(false);
    }
  }, [masterContract, web3Provider, userWeb3Addresses]);

  useEffect(() => {
    getStakeForVotingData();
  }, [getStakeForVotingData]);

  const gotoStakeForOption = useCallback(() => {
    navigate("/stake-for-option");
  }, [navigate]);

  const gotoStakeForVoting = useCallback(() => {
    if ((userWeb3Addresses?.length ?? 0) > 0) {
      navigate("/stake-for-voting");
    }
  }, [userWeb3Addresses, navigate]);

  const btnConnectWalletOnClick = (e) => {
    e.stopPropagation();
    setIsShowModalConnectWallet(true);
  };

  const linkConnectOtherWalletOnClick = (e) => {
    e.stopPropagation();
    setIsShowModalConnectWallet(true);
  };

  const renderStakeBlock = ({
    title,
    icon,
    content,
    footer,
    network,
    actionButton,
    onClick,
  }) => (
    <div
      className="stake-block flex-1 py-[30px] px-5 lg:px-[30px] rounded-lg bg-white lg:max-w-[660px] min-h-[230px] w-full relative cursor-pointer"
      onClick={onClick}
    >
      <div className="stake-block__header flex justify-between items-center flex-wrap gap-4">
        <div className="stake-block__header__title flex items-center gap-2">
          <span className="text-[24px] lg:text-[32px]">{icon}</span>
          <span className="text-[20px]/[24px] lg:text-[24px]/[32px] font-semibold capitalize">
            {title}
          </span>
        </div>
        <div className="stake-block__header__network">
          {network === "polygon" ? (
            <div className="text-[10px]/[16px] lg:text-[12px]/[24px] text-white bg-[#7950DD80] font-semibold p-1 rounded-full flex gap-1 items-center">
              <PolygonIcon
                width={isMobile ? 16 : 24}
                height={isMobile ? 16 : 24}
              />
              {T("Polygon Network")}
            </div>
          ) : (
            <div className="text-[10px]/[16px] lg:text-[12px]/[24px] text-white bg-[#627EEA80] font-semibold p-1 rounded-full flex gap-1 items-center">
              <EtherIcon
                width={isMobile ? 16 : 24}
                height={isMobile ? 16 : 24}
              />
              {T("Ethereum Network")}
            </div>
          )}
        </div>
      </div>
      <div className="stake-block__content mt-5">{content}</div>
      <div className="stake-block__action flex mt-5 justify-end">
        {actionButton}
      </div>
      {footer}
    </div>
  );

  const renderBlockStakeForOption = () => {
    const { num: stakedBalance, suffix: stakedBalanceSuffix } =
      formatNumberShort(stakeForOptionData.stakedBalance, false);

    const { num: availableBalance, suffix: availableBalanceSuffix } =
      formatNumberShort(stakeForOptionData.availableBalance, false);
    return renderStakeBlock({
      title: T("Stake for option"),
      icon: <StakeForOptionIcon />,
      onClick: gotoStakeForOption,
      actionButton: (
        <button
          className="text-[#F12E11] flex gap-1 items-center text-[12px] lg:text-[16px]"
          onClick={(e) => {
            e.stopPropagation();
            gotoStakeForOption();
          }}
        >
          {T("View Detail")}{" "}
          <span className="text-[16px] lg:text-[24px]">
            <ArrowRightIcon2 />
          </span>
        </button>
      ),
      network: "polygon",
      content: (
        <div className="flex flex-col lg:items-center lg:justify-center lg:flex-row gap-2 lg:gap-10">
          <div className="flex flex-row justify-between items-center lg:flex-col-reverse">
            <div className="text-[14px] lg:text-[16px]">
              {T("Active Stake Balance")}
            </div>
            <div className="text-[25px] font-semibold lg:text-[40px] text-[#F12E11] whitespace-nowrap">
              {isLoadingStakeForOption ? (
                <div role="status" className="max-w-sm animate-pulse">
                  <div className="h-[30px] lg:h-[50px] bg-gray-200 rounded-full dark:bg-gray-700 w-[120px] mb-2"></div>
                </div>
              ) : (
                <NumberFormat
                  value={stakedBalance}
                  displayType={"text"}
                  thousandSeparator={thousandSeparatorByCurrency(getCurrency())}
                  decimalSeparator={decimalSeparatorByCurrency(getCurrency())}
                  suffix={`${stakedBalanceSuffix} SGT`}
                />
              )}
            </div>
          </div>
          <div className="flex flex-row justify-between items-center lg:flex-col-reverse">
            <div className="text-[14px] lg:text-[16px]">
              {T("Eligible Stake Balance")}
            </div>
            <div className="text-[25px] font-semibold lg:text-[40px] text-[#F12E11] whitespace-nowrap">
              {isLoadingStakeForOption ? (
                <div role="status" className="max-w-sm animate-pulse">
                  <div className="h-[30px] lg:h-[50px] bg-gray-200 rounded-full dark:bg-gray-700 w-[120px] mb-2"></div>
                </div>
              ) : (
                <NumberFormat
                  value={availableBalance}
                  displayType={"text"}
                  thousandSeparator={thousandSeparatorByCurrency(getCurrency())}
                  decimalSeparator={decimalSeparatorByCurrency(getCurrency())}
                  suffix={`${availableBalanceSuffix} SGT`}
                />
              )}
            </div>
          </div>
        </div>
      ),
    });
  };

  const renderBlockStakeForVoting = () => {
    const { num: stakedBalance, suffix: stakedBalanceSuffix } =
      formatNumberShort(stakeForVotingData.stakedBalance, false);
    return renderStakeBlock({
      title: T("Stake for voting"),
      icon: <StakeForVotingIcon />,
      onClick: gotoStakeForVoting,
      actionButton: <></>,
      network: "eth",
      content:
        (userWeb3Addresses?.length ?? 0) > 0 ? (
          <>
            <div className="flex flex-col lg:items-center lg:justify-center lg:flex-row gap-2 lg:gap-10">
              <div className="flex flex-row justify-between items-center lg:flex-col-reverse">
                <div className="text-[14px] lg:text-[16px]">
                  {T("Active Stake Balance")}
                </div>
                <div className="text-[25px] font-semibold lg:text-[40px] text-[#F12E11] whitespace-nowrap">
                  {isLoadingStakeForVoting ? (
                    <div role="status" className="max-w-sm animate-pulse">
                      <div className="h-[30px] lg:h-[50px] bg-gray-200 rounded-full dark:bg-gray-700 w-[120px] mb-2"></div>
                    </div>
                  ) : (
                    <NumberFormat
                      value={stakedBalance}
                      displayType={"text"}
                      thousandSeparator={thousandSeparatorByCurrency(
                        getCurrency()
                      )}
                      decimalSeparator={decimalSeparatorByCurrency(
                        getCurrency()
                      )}
                      suffix={`${stakedBalanceSuffix} SGT`}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-between items-center lg:flex-col-reverse">
                <div className="text-[14px] lg:text-[16px]">
                  {T("Total xSGT NFT")}
                </div>
                <div className="text-[25px] font-semibold lg:text-[40px] text-[#F12E11] whitespace-nowrap">
                  {isLoadingStakeForVoting ? (
                    <div role="status" className="max-w-sm animate-pulse">
                      <div className="h-[30px] lg:h-[50px] bg-gray-200 rounded-full dark:bg-gray-700 w-[120px] mb-2"></div>
                    </div>
                  ) : (
                    <NumberFormat
                      value={stakeForVotingData.totalNfts}
                      displayType={"text"}
                      thousandSeparator={thousandSeparatorByCurrency(
                        getCurrency()
                      )}
                      decimalSeparator={decimalSeparatorByCurrency(
                        getCurrency()
                      )}
                      suffix={" NFTs"}
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-center mt-5">
                {!addressConnected && (
                  <button
                    className="btn-gradient !h-[30px] lg:!h-[44px] !text-[10px] lg:!text-[14px] w-full"
                    onClick={btnConnectWalletOnClick}
                  >
                    {T("Connect Wallet")}
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div>
            <div>{parse(T("stake_for_voting_desc"))}</div>
            <div className="flex justify-center mt-5">
              {!addressConnected && (
                <button
                  className="btn-gradient !h-[30px] lg:!h-[44px] !text-[10px] lg:!text-[14px] w-full"
                  onClick={btnConnectWalletOnClick}
                >
                  {T("Connect Wallet")}
                </button>
              )}
            </div>
            <div className="flex items-start mt-5 p-2 gap-2 rounded-lg">
              <span>
                <WarningIcon />
              </span>
              <span>{parse(T("stake_for_voting_warning"))}</span>
            </div>
          </div>
        ),
      footer: (userWeb3Addresses?.length ?? 0) > 0 && addressConnected && (
        <div className="stake-block__bottom absolute right-0 left-0 bottom-0 bg-[#F6E5CE] rounded-b-[8px] py-2 px-5 lg:px-[30px]">
          <div className="text-[10px] lg:text-[14px]">
            <span>{T("Having another wallet?")}</span>
            <span>&nbsp;</span>
            <button
              className="text-[#F12E11] font-semibold cursor-pointer"
              onClick={linkConnectOtherWalletOnClick}
            >
              {T("Connect now")}
            </button>
          </div>
        </div>
      ),
    });
  };

  return (
    <Container>
      <ContainerView>
        <div className="stake__title text-[24px] font-bold lg:text-[34px] lg:font-semibold mb-5 lg:mb-[30px]">
          {T("Stake")}
        </div>
        <div className="stake__body">
          <div className="flex flex-col justify-center gap-[20px] lg:flex-row lg:gap[30px] items-stretch">
            {renderBlockStakeForOption()}
            {renderBlockStakeForVoting()}
          </div>
        </div>
      </ContainerView>
      {isShowModalConnectWallet && (
        <ModalConnectWallet
          onClose={() => setIsShowModalConnectWallet(false)}
          onConnected={gotoStakeForVoting}
        />
      )}
    </Container>
  );
}
